import { getWindow } from '@surfline/web-common';

type Properties = {
  [key: string]: string | number | boolean | null | undefined | ReadableStream<Uint8Array>;
};

/**
 * Reports an error to NewRelic, handling both client and server-side environments.
 *
 * @param error - The error to report
 * @param source - The source of the error (e.g., function or component name)
 * @param properties - Additional properties to include with the error report
 * @param isCustomEvent - Whether to report the error as a custom event
 */
const reportErrorToNewRelic = (
  error: Error,
  source: string,
  properties: Properties = {},
  isCustomEvent: boolean = false,
): void => {
  const errorContext = {
    source,
    ...properties,
    message: error?.message ?? error?.toString() ?? 'Unknown error',
  };

  const win = getWindow();
  if (win) {
    // Client-side error reporting
    if (isCustomEvent) {
      win?.newrelic?.recordCustomEvent(`custom_${source}`, { error, ...errorContext });
    } else {
      win?.newrelic?.noticeError(error, errorContext);
    }
  } else {
    // Server-side error reporting
    import('newrelic')
      .then((newrelicModule) => {
        if (isCustomEvent) {
          newrelicModule.default.recordCustomEvent(`custom_${source}`, {
            ...errorContext,
          });
        } else {
          newrelicModule.default.noticeError(error, errorContext);
        }
      })
      .catch((importError) => {
        console.error('Failed to import newrelic:', importError);
      });
  }
};

export default reportErrorToNewRelic;
